// frontend/src/components/admin/PermissionManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import permissionService from '../../services/permissionService';

/**
 * Composant pour la gestion des permissions
 * @returns {React.ReactNode} - Composant de gestion des permissions
 */
const PermissionManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' ou 'edit'
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: ''
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Charger les permissions au montage du composant
  useEffect(() => {
    fetchPermissions();
  }, []);

  /**
   * Récupérer toutes les permissions
   */
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAllPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      showNotification('Erreur lors du chargement des permissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ouvrir le dialogue de création
   */
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setFormData({
      name: '',
      description: '',
      category: ''
    });
    setOpenDialog(true);
  };

  /**
   * Ouvrir le dialogue d'édition
   * @param {Object} permission - Permission à éditer
   */
  const handleOpenEditDialog = (permission) => {
    setDialogMode('edit');
    setSelectedPermission(permission);
    setFormData({
      name: permission.name,
      description: permission.description || '',
      category: permission.category || ''
    });
    setOpenDialog(true);
  };

  /**
   * Fermer le dialogue
   */
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPermission(null);
  };

  /**
   * Gérer les changements dans le formulaire
   * @param {Object} e - Événement de changement
   */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await permissionService.createPermission(formData);
        showNotification('Permission créée avec succès', 'success');
      } else {
        await permissionService.updatePermission(selectedPermission.id, formData);
        showNotification('Permission mise à jour avec succès', 'success');
      }
      
      handleCloseDialog();
      fetchPermissions();
    } catch (error) {
      console.error('Error submitting form:', error);
      showNotification(
        `Erreur lors de la ${dialogMode === 'create' ? 'création' : 'mise à jour'} de la permission`,
        'error'
      );
    }
  };

  /**
   * Supprimer une permission
   * @param {Object} permission - Permission à supprimer
   */
  const handleDeletePermission = async (permission) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la permission "${permission.name}" ?`)) {
      try {
        await permissionService.deletePermission(permission.id);
        showNotification('Permission supprimée avec succès', 'success');
        fetchPermissions();
      } catch (error) {
        console.error('Error deleting permission:', error);
        showNotification('Erreur lors de la suppression de la permission', 'error');
      }
    }
  };

  /**
   * Afficher une notification
   * @param {string} message - Message à afficher
   * @param {string} severity - Type de notification ('success', 'error', 'warning', 'info')
   */
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  /**
   * Fermer la notification
   */
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Obtenir les catégories uniques pour le filtre
  const uniqueCategories = [...new Set(permissions.map(p => p.category).filter(Boolean))];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Gestion des Permissions
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Nouvelle Permission
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>{permission.name}</TableCell>
                  <TableCell>{permission.description}</TableCell>
                  <TableCell>{permission.category}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditDialog(permission)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeletePermission(permission)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {permissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucune permission trouvée
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogue de création/édition */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Nouvelle Permission' : 'Modifier Permission'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Catégorie</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                label="Catégorie"
              >
                <MenuItem value="">
                  <em>Aucune</em>
                </MenuItem>
                {uniqueCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
                <MenuItem value="new">
                  <em>Nouvelle catégorie...</em>
                </MenuItem>
              </Select>
            </FormControl>
            {formData.category === 'new' && (
              <TextField
                fullWidth
                label="Nouvelle catégorie"
                name="newCategory"
                value={formData.newCategory || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    newCategory: e.target.value,
                    category: e.target.value
                  }));
                }}
                margin="normal"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {dialogMode === 'create' ? 'Créer' : 'Mettre à jour'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionManagement;